import { useQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';

const GET_DASHBOARD_STATS = gql`
  query GetDashboardStats {
    listInquiries(limit: 1000) {
      items {
        id
        status
      }
    }
    listCases(limit: 1000) {
      items {
        id
        status
      }
    }
  }
`;

interface InquiryItem {
  id: string;
  status: string;
}

interface CaseItem {
  id: string;
  status: string;
}

interface DashboardStatsData {
  listInquiries: {
    items: InquiryItem[];
  };
  listCases: {
    items: CaseItem[];
  };
}

export function StatsWidget() {
  const navigate = useNavigate();
  const { data, loading } = useQuery<DashboardStatsData>(GET_DASHBOARD_STATS, {
    fetchPolicy: 'network-only',
  });

  const inquiries = data?.listInquiries?.items || [];
  const cases = data?.listCases?.items || [];

  const stats = [
    {
      label: 'New Inquiries',
      value: inquiries.filter((i: any) => i.status === 'NEW').length,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      onClick: () => navigate('/dashboard'), // Navigate to dashboard which shows inquiry queue
    },
    {
      label: 'Active Cases',
      value: cases.filter((c: any) => c.status === 'IN_PROGRESS' || c.status === 'NEGOTIATION').length,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
      onClick: () => navigate('/dashboard'), // Navigate to dashboard which shows cases
    },
    {
      label: 'Accepted Leads',
      value: inquiries.filter((i: any) => i.status === 'ACCEPTED').length,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      onClick: () => navigate('/dashboard'), // Navigate to dashboard which shows inquiry queue
    },
    {
      label: 'Total Cases',
      value: cases.length,
      color: 'text-aalaw-red',
      bgColor: 'bg-red-50',
      onClick: () => navigate('/dashboard'), // Navigate to dashboard which shows cases
    },
  ];

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map((i) => (
          <Card key={i}>
            <CardContent className="p-6">
              <div className="animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-20 mb-2"></div>
                <div className="h-8 bg-gray-200 rounded w-12"></div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {stats.map((stat, index) => (
        <Card 
          key={index} 
          className="inquiry-card cursor-pointer hover:shadow-lg transition-shadow"
          onClick={stat.onClick}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-medium">{stat.label}</p>
                <p className={`text-3xl font-bold mt-2 ${stat.color}`}>{stat.value}</p>
              </div>
              <div className={`w-12 h-12 rounded-full ${stat.bgColor} flex items-center justify-center`}>
                <span className={`text-2xl ${stat.color}`}>{stat.value}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
